import type { TaskGroup, TaskItem, TaskKind, TaskSection } from '@felix/domain';
import { TASK_SECTIONS } from '@felix/domain';
import {
  IconArrowBackUp,
  IconCash,
  IconClipboardCheck,
  IconFileText,
  IconShieldCheck,
  IconShoppingCart,
  IconTool,
} from '@tabler/icons-react';
import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../../StatusBadge.js';
import { followLink, useShell } from '../../shell-context.js';
import { recordTime } from '../forms/status.js';
import { formatMoneyWith } from '../payroll/view.js';
import classes from './TasksPanel.module.css';
import { taskListPath, taskPath, UPCOMING_FORMS_PATH } from './task-path.js';

/**
 * Bölümün ikonu (ADR 0156 "Görünüm"): iade, finans, satın alma, teknik, HSEQ, doküman, ISM form.
 * Anahtar `TaskSection`: yeni bölümün ikonu yoksa derleme kırılır.
 */
const SECTION_ICON: Readonly<Record<TaskSection, typeof IconCash>> = {
  returned: IconArrowBackUp,
  finance: IconCash,
  purchasing: IconShoppingCart,
  technical: IconTool,
  hseq: IconShieldCheck,
  documents: IconFileText,
  ism_forms: IconClipboardCheck,
};

export function kindIcon(kind: TaskKind, size = 16): ReactNode {
  const Icon = SECTION_ICON[TASK_SECTIONS[kind]];
  return <Icon size={size} stroke={1.8} aria-hidden />;
}

/** Uygulama içi bağlantı: kabuk varsa onunla gider, yoksa tarayıcının bağlantısı. */
export function TaskLink({
  path,
  className,
  children,
}: {
  path: string;
  className?: string | undefined;
  children: ReactNode;
}) {
  const shell = useShell();
  return (
    <a
      href={path}
      className={className}
      onClick={
        shell
          ? (event: MouseEvent<HTMLAnchorElement>) => followLink(event, path, shell.navigate)
          : undefined
      }
    >
      {children}
    </a>
  );
}

/** İş satırı: kayıt numarası (yoksa adı), gemi, tutar, adım, rozetler ve kişiye düştüğü an. */
export function TaskRow({ kind, item }: { kind: TaskKind; item: TaskItem }) {
  const { t, i18n } = useTranslation();
  const name = item.recordNo ?? item.title ?? t(`tasks.kind.${kind}`);
  const details = [
    item.recordNo && item.title ? item.title : null,
    item.variant && i18n.exists(`tasks.variant.${item.variant}`)
      ? t(`tasks.variant.${item.variant}`)
      : null,
    item.vesselName,
    item.amount
      ? formatMoneyWith(item.amount.minor, item.amount.currency, i18n.resolvedLanguage ?? 'en')
      : null,
    item.step ? t('tasks.step', { step: item.step }) : null,
  ].filter((part): part is string => Boolean(part));
  // Satırın bütünü bir bağlantıdır (dar ekranda en az 44 px dokunma hedefi; ADR 0156 "Duyarlılık")
  return (
    <li>
      <TaskLink path={taskPath(kind, item)} className={classes.row}>
        <span className={classes.rowMain} data-overdue={item.overdue || undefined}>
          <span className={classes.recordLink}>{name}</span>
          {details.length > 0 ? (
            <span className={classes.details}>{details.join(' · ')}</span>
          ) : null}
          {kind === 'returned' && item.note ? (
            <span className={classes.details}>{item.note}</span>
          ) : null}
        </span>
        <span className={classes.rowSide}>
          {item.overdue ? (
            <StatusBadge tone="danger">{t('tasks.badges.overdue')}</StatusBadge>
          ) : null}
          {item.escalated ? (
            <StatusBadge tone="warning">{t('tasks.badges.escalated')}</StatusBadge>
          ) : null}
          {kind === 'returned' ? (
            <StatusBadge tone="warning">{t('tasks.badges.returned')}</StatusBadge>
          ) : null}
          <time className={classes.time} dateTime={item.since}>
            {recordTime(item.since, null)}
          </time>
        </span>
      </TaskLink>
    </li>
  );
}

/**
 * İş grubu: ikon, türün adı, sayı ve "Tümünü gör"; en çok üç satır, kalanı "+N daha". ISM form
 * grubunda sırası gelmemiş adımlar ayrı bağlantıdır, sayaca girmez (#2238).
 */
export function TaskGroupView({ group, compact = false }: { group: TaskGroup; compact?: boolean }) {
  const { t } = useTranslation();
  const list = taskListPath(group.kind, group.items);
  const rest = group.count - group.items.length;
  const titleId = `task-group-${group.kind}${compact ? '-bell' : ''}`;
  return (
    <section
      className={classes.group}
      aria-labelledby={titleId}
      data-compact={compact || undefined}
    >
      <div className={classes.groupHead}>
        <span className={classes.groupIcon}>{kindIcon(group.kind)}</span>
        {/* Sayı başlığın içinde: ekran okuyucu grubu sayısıyla okur */}
        <h4 id={titleId} className={classes.groupTitle}>
          <span className={classes.groupName}>{t(`tasks.kind.${group.kind}`)}</span>
          {group.count > 0 ? (
            <StatusBadge tone={group.overdue > 0 ? 'danger' : 'info'}>{group.count}</StatusBadge>
          ) : null}
        </h4>
        {list && !compact ? (
          <TaskLink path={list} className={classes.viewAll}>
            {t('tasks.viewAll')}
          </TaskLink>
        ) : null}
      </div>
      {group.items.length > 0 ? (
        <ul className={classes.rows}>
          {group.items.map((item) => (
            <TaskRow
              key={`${item.recordId}-${item.variant ?? ''}-${item.step ?? ''}`}
              kind={group.kind}
              item={item}
            />
          ))}
        </ul>
      ) : null}
      {rest > 0 ? (
        list ? (
          <TaskLink path={list} className={classes.more}>
            {t('tasks.more', { count: rest })}
          </TaskLink>
        ) : (
          <span className={classes.more}>{t('tasks.more', { count: rest })}</span>
        )
      ) : null}
      {group.upcoming ? (
        <TaskLink path={UPCOMING_FORMS_PATH} className={classes.more}>
          {t('tasks.upcoming', { count: group.upcoming })}
        </TaskLink>
      ) : null}
    </section>
  );
}
